import { FixedHeaderLayout } from "components/FixedHeaderLayout/FixedHeaderLayout"
import { ProductCategories, ProductCategoriesLink } from "components/Navbar/types"
import PageLoader from "components/PageLoader"
import { useAppSelector } from "hooks/storeHooks"
import { useAlert } from "providers/AlertProvider"
import { useCurrentId } from "hooks/useCurrentId"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import EffectSettings from "services/effect_settings"
import { tintVTOService } from "services/tintvto"
import { IntegrationType, type ProductV2 } from "types"
import { CATEGORIES } from "./mock"
import { Beauty } from "services/player"
import { useSaveProductMutation } from "hooks/query/useProductsQuery"
import { z } from "zod"
import { selectTypes } from "store/product/productSlice"
import { ProductTranslationsModal } from "components/ProductTranslations/ProductTranslationsModal"
import { type ProductTranslationsData } from "components/ProductTranslations/ProductTranslations"
import { MakeupMetadataCard } from "./MakeupMetadataCard"
import { MakeupPreviewPanel } from "./MakeupPreviewPanel"
import { MakeupVariantsCard } from "./MakeupVariantsCard"
import UnsavedChangesDialog from "components/UnsavedChangesDialog"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"

const initialProductItem: ProductWithColors = {
  brandSlug: "",
  categoryId: 0,
  coverage: "",
  finish: "",
  id: "",
  merchant: "",
  region: "",
  sku: "",
  type: "",
  upc: "",
  url: "",
  brand: "",
  name: "",
  colors: [],
  cover: "",
}

const PRODUCT_TYPE_REGIONS: Record<string, string[]> = {
  EYELINER: ["eyeliner"],
  EYESHADOW: ["eyeshadow"],
  MASCARA: ["eyelashes"],
  BLUSH: ["blush"],
  FOUNDATION: ["foundation"],
  LIPSTICK: ["lipstick"],
  EYEBROWS: ["eyebrows"],
  HIGHLIGHTER: ["highlighter"],
  CONTOUR: ["contour"],
  CONCEALER: ["concealer"],
  EYE_COLOR: ["eyeshadow"],
  LIPLINER: ["lipsliner"],
  GLOSS: ["lipstick"],
  BB_CC_CREAM: ["foundation"],
  POWDER: ["foundation"],
  EYES_AND_BROWS: ["eyebrows", "eyeshadow"],
  CHEEKS: ["blush", "contour"],
  BRONZER: ["blush", "contour"],
  PRIMERS: ["foundation"],
  LIP_BALM: ["lipstic"],
  FOUNDATION_CLEAR: ["concealer", "foundation"],
  FOUNDATION_MEDIUM_CLEAR: ["concealer", "foundation"],
  FOUNDATION_MEDIUM: ["concealer", "foundation"],
  FOUNDATION_MEDIUM_DARK: ["concealer", "foundation"],
  FOUNDATION_DARK: ["concealer", "foundation"],
}

interface ProductPageProps {
  add?: boolean
}

let id = 0
const createId = () => (id++).toString()

const createEmptyColor = () => ({
  id: createId(),
  color: "#ececec",
  upc: "",
  sku: "",
  name: "",
})

type ProductWithColors = Omit<ProductV2, "colors"> & {
  colors: Array<ProductV2["colors"][0] & { id?: string }>
}

const cloneProductItem = (item: ProductWithColors): ProductWithColors => ({
  ...item,
  colors: item.colors.map((color) => ({ ...color })),
})

const withEditorColorIds = (item: ProductWithColors): ProductWithColors => ({
  ...item,
  colors: item.colors.map((color) => ({ ...color, id: color.id ?? createId() })),
})

const normalizeComparableString = (value?: string | null) => (value ?? "").trim()

const normalizeComparableColor = (value?: string | null) =>
  normalizeComparableString(value).replaceAll(",", "").replaceAll(" ", "").toLowerCase()

const getStableColorKey = (color?: ProductWithColors["colors"][number]) =>
  color
    ? normalizeComparableString(color.skuId) ||
      normalizeComparableString(color.sku) ||
      normalizeComparableString(color.externalId)
    : ""

const getSavedColorKeyCandidates = (color: ProductWithColors["colors"][number]) =>
  [color.skuId, color.sku, color.externalId].map(normalizeComparableString).filter(Boolean)

const optionalColorFieldMatches = (editedValue?: string | null, savedValue?: string | null) => {
  const edited = normalizeComparableString(editedValue)
  if (!edited) return true
  return edited === normalizeComparableString(savedValue)
}

const colorRowsMatch = (
  editedColor: ProductWithColors["colors"][number],
  savedColor: ProductWithColors["colors"][number]
) =>
  normalizeComparableString(editedColor.name) === normalizeComparableString(savedColor.name) &&
  normalizeComparableString(editedColor.sku) === normalizeComparableString(savedColor.sku) &&
  normalizeComparableString(editedColor.upc) === normalizeComparableString(savedColor.upc) &&
  normalizeComparableString(editedColor.externalId) ===
    normalizeComparableString(savedColor.externalId) &&
  normalizeComparableColor(editedColor.color) === normalizeComparableColor(savedColor.color)

const keylessColorRowsMatch = (
  editedColor: ProductWithColors["colors"][number],
  savedColor: ProductWithColors["colors"][number]
) =>
  normalizeComparableString(editedColor.name) === normalizeComparableString(savedColor.name) &&
  normalizeComparableColor(editedColor.color) === normalizeComparableColor(savedColor.color) &&
  optionalColorFieldMatches(editedColor.sku, savedColor.sku) &&
  optionalColorFieldMatches(editedColor.upc, savedColor.upc) &&
  optionalColorFieldMatches(editedColor.externalId, savedColor.externalId)

const findSavedColorMatchIndex = (
  editedColor: ProductWithColors["colors"][number],
  unmatchedSavedColors: ProductWithColors["colors"]
) => {
  const stableKey = getStableColorKey(editedColor)
  if (stableKey)
    return unmatchedSavedColors.findIndex((savedColor) =>
      getSavedColorKeyCandidates(savedColor).includes(stableKey)
    )

  return unmatchedSavedColors.findIndex((savedColor) =>
    keylessColorRowsMatch(editedColor, savedColor)
  )
}

const serverProductMatchesEditedFields = (
  editedProduct: ProductWithColors,
  savedProduct: ProductWithColors
) => {
  const scalarFieldsMatch =
    normalizeComparableString(editedProduct.name) ===
      normalizeComparableString(savedProduct.name) &&
    normalizeComparableString(editedProduct.brand) ===
      normalizeComparableString(savedProduct.brand) &&
    normalizeComparableString(editedProduct.typeId) ===
      normalizeComparableString(savedProduct.typeId) &&
    normalizeComparableString(editedProduct.region) ===
      normalizeComparableString(savedProduct.region) &&
    normalizeComparableString(editedProduct.finish) ===
      normalizeComparableString(savedProduct.finish) &&
    normalizeComparableString(editedProduct.coverage) ===
      normalizeComparableString(savedProduct.coverage) &&
    normalizeComparableString(editedProduct.merchant) ===
      normalizeComparableString(savedProduct.merchant)

  if (!scalarFieldsMatch || editedProduct.colors.length !== savedProduct.colors.length) return false

  const unmatchedSavedColors = [...savedProduct.colors]

  return editedProduct.colors.every((editedColor) => {
    const matchIndex = findSavedColorMatchIndex(editedColor, unmatchedSavedColors)

    if (matchIndex < 0) return false

    const [savedColor] = unmatchedSavedColors.splice(matchIndex, 1)
    return getStableColorKey(editedColor)
      ? colorRowsMatch(editedColor, savedColor)
      : keylessColorRowsMatch(editedColor, savedColor)
  })
}

const createProductSnapshot = (item: ProductWithColors) =>
  JSON.stringify({
    brand: item.brand,
    brandSlug: item.brandSlug,
    categoryId: item.categoryId,
    colorCount: item.colors.length,
    colors: item.colors.map(({ color, externalId, name, sku, skuId, upc }) => ({
      color,
      externalId: externalId ?? "",
      name,
      sku,
      skuId: skuId ?? "",
      upc: upc ?? "",
    })),
    cover: item.cover,
    coverage: item.coverage,
    externalId: item.externalId ?? "",
    finish: item.finish,
    id: item.id,
    merchant: item.merchant,
    name: item.name,
    region: item.region,
    sku: item.sku,
    type: item.type,
    typeId: item.typeId ?? "",
    upc: item.upc,
    url: item.url,
  })

type EffectSettingsMap = Record<
  string,
  Record<
    string,
    {
      K?: Record<string, number>
    }
  >
>

const createProductSchema = (t: (key: string) => string) => {
  const colorSchema = z.object({
    color: z.string().min(1, t("errors.colorRequired")),
    name: z.string().min(1, t("errors.colorNameRequired")),
  })

  return z.object({
    name: z.string().min(1, t("errors.nameRequired")),
    brand: z.string(),
    typeId: z.string().min(1, t("errors.typeRequired")),
    cover: z.string(),
    region: z.string().min(1, t("errors.regionRequired")),
    finish: z.string().min(1, t("errors.finishRequired")),
    coverage: z.string().min(1, t("errors.coverageRequired")),
    colors: z.array(colorSchema).min(1, t("errors.atLeastOneColorRequired")),
    merchant: z.string().min(1, t("errors.atLeastOneMerchantRequired")),
  })
}

export const ProductPage = ({ add }: ProductPageProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const userInfo = useAppSelector((state) => state.user.userInfo)
  const selectedProduct = useAppSelector((state) => state.products.product)
  const types = useAppSelector(selectTypes)
  const productId = useCurrentId()

  const [productItem, setProductItem] = useState<ProductWithColors>(initialProductItem)
  const [merchantIds, setMerchantIds] = useState<Array<{ id: string; name: string }>>([])
  const [activeColorId, setActiveColorId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [translations, setTranslations] = useState<ProductTranslationsData>({
    productName: [],
    skuNames: {},
  })
  const [isTranslationsModalOpen, setIsTranslationsModalOpen] = useState(false)
  const [pendingTranslations, setPendingTranslations] = useState<ProductTranslationsData>({
    productName: [],
    skuNames: {},
  })
  const [translationsModified, setTranslationsModified] = useState(false)
  const [savedLanguages, setSavedLanguages] = useState<string[]>([])
  const [savedProductItem, setSavedProductItem] = useState<ProductWithColors | null>(null)
  const [savedProductSnapshot, setSavedProductSnapshot] = useState<string | null>(null)
  const [visibilityLoading, setVisibilityLoading] = useState(false)

  const refetchTranslations = async () => {
    if (!productId || !productItem.colors?.length) return
    const skuIds = productItem.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
    if (skuIds.length > 0)
      try {
        const translationsData = await tintVTOService.getProductTranslations(productId, skuIds)
        const loadedProductName = translationsData.alternateName || []
        const loadedSkuNames = (translationsData.alternateSKUsName || []).reduce(
          (
            acc: Record<string, Array<{ code: string; name: string }>>,
            item: { skuId: string; skuI8N?: Array<{ code: string; name: string }> }
          ) => {
            acc[item.skuId] = item.skuI8N || []
            return acc
          },
          {} as Record<string, Array<{ code: string; name: string }>>
        )

        setTranslations({
          productName: loadedProductName,
          skuNames: loadedSkuNames,
        })
        setSavedLanguages(loadedProductName.map((t: { code: string; name: string }) => t.code))
      } catch (error) {
        // Failed to load translations
      }
  }
  const sortedTypes = [...types].sort((a, b) => a.name.localeCompare(b.name))
  const getRegionsFrom = (item: Partial<ProductWithColors>) =>
    types.find((t) => t.id === item.typeId)?.name
      ? PRODUCT_TYPE_REGIONS[types.find((t) => t.id === item.typeId)!.name] || []
      : []
  const productSchema = createProductSchema(t)
  const [errors, setErrors] = useState<z.ZodFormattedError<z.infer<typeof productSchema>> | null>(
    null
  )
  const effectSettings = EffectSettings as EffectSettingsMap
  const pageHeaderTitle = t("productPage.pageTitle")
  const normalizeLoadedProduct = (product: ProductV2): ProductWithColors =>
    withEditorColorIds({
      ...product,
      typeId: types.find(({ name }) => name === product.type)?.id,
      categoryId: CATEGORIES[0].id,
      cover: product.cover,
      merchant: product.merchant?.length ? product.merchant : userInfo.merchantId,
    })

  const applySavedProductBaseline = (item: ProductWithColors) => {
    const savedItem = cloneProductItem(item)
    setSavedProductItem(savedItem)
    setSavedProductSnapshot(createProductSnapshot(savedItem))
  }

  const getFinishesFrom = (item: Partial<ProductWithColors>) =>
    Object.keys(item.region ? effectSettings[item.region] || {} : {})
  const getCoveragesFrom = (item: Partial<ProductWithColors>) =>
    Object.keys(
      item.region && item.finish ? effectSettings[item.region]?.[item.finish]?.K || {} : {}
    )

  const updateProductItem = (newProps: Partial<ProductWithColors>) => {
    setProductItem((prev) => {
      const colorsWithIds =
        { ...prev, ...newProps }.colors?.map((c) =>
          c.id
            ? c
            : {
                id: createId(),
                ...c,
              }
        ) || []

      setActiveColorId(colorsWithIds[0]?.id || null)

      const updated = { ...prev, ...newProps, colors: colorsWithIds }

      if (activeColorId === null && updated.colors?.length > 0)
        setActiveColorId(updated.colors[0]?.id ?? null)
      if (updated.typeId && !updated.region) updated.region = getRegionsFrom(updated)[0] || ""
      if (updated.region && !updated.finish) updated.finish = getFinishesFrom(updated)[0] || ""
      if (updated.finish && !updated.coverage) updated.coverage = getCoveragesFrom(updated)[0] || ""

      return updated
    })
  }
  const [showValidation, setShowValidation] = useState(false)
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)
  // Errors are only surfaced after the first save attempt — never on a fresh form.
  const displayedErrors = showValidation ? errors : null

  const validateForm = () => {
    if (isLoading) return true

    try {
      productSchema.parse(productItem)
      setErrors(null)
      return true
    } catch (error) {
      if (error instanceof z.ZodError) setErrors(error.format())
      return false
    }
  }

  useEffect(() => {
    validateForm()
  }, [productItem])

  useEffect(() => {
    if (!add) return

    const currentSkuIds = productItem.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
    const existingSkuIds = Object.keys(pendingTranslations.skuNames)

    const newSkuIds = currentSkuIds.filter((skuId) => !existingSkuIds.includes(skuId))

    if (newSkuIds.length > 0 && pendingTranslations.productName.length > 0)
      setPendingTranslations((prev) => {
        const updatedSkuNames = { ...prev.skuNames }

        newSkuIds.forEach((skuId) => {
          updatedSkuNames[skuId] = prev.productName.map((t) => ({
            code: t.code,
            name: "",
          }))
        })

        return {
          ...prev,
          skuNames: updatedSkuNames,
        }
      })
  }, [productItem.colors?.map((c) => c.skuId || c.sku).join(","), add])
  useEffect(() => {
    ;(async () => {
      setIsLoading(true)

      if (userInfo.groups.includes("Admin")) {
        const { data } = await tintVTOService.merchants()
        setMerchantIds(
          data?.merchants?.map((m: { id: string; name: string }) => ({ id: m.id, name: m.name })) ||
            []
        )
      }

      if (productId && !add) {
        setSavedProductItem(null)
        setSavedProductSnapshot(null)
        const product = await tintVTOService.productByIdV2(productId)
        const normalizedProduct = normalizeLoadedProduct(product)
        setProductItem(normalizedProduct)
        setActiveColorId(normalizedProduct.colors[0]?.id ?? null)
        applySavedProductBaseline(normalizedProduct)

        const skuIds = product.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
        if (skuIds.length > 0)
          try {
            const translationsData = await tintVTOService.getProductTranslations(productId, skuIds)
            const loadedProductName = translationsData.alternateName || []
            const loadedSkuNames = (translationsData.alternateSKUsName || []).reduce(
              (
                acc: Record<string, Array<{ code: string; name: string }>>,
                item: { skuId: string; skuI8N?: Array<{ code: string; name: string }> }
              ) => {
                acc[item.skuId] = item.skuI8N || []
                return acc
              },
              {} as Record<string, Array<{ code: string; name: string }>>
            )

            setTranslations({
              productName: loadedProductName,
              skuNames: loadedSkuNames,
            })
            setSavedLanguages(loadedProductName.map((t: { code: string; name: string }) => t.code))
          } catch (error) {
            // Failed to load translations
          }
      } else if (userInfo.integrationType !== IntegrationType.COMMON) {
        if (selectedProduct)
          updateProductItem({
            ...selectedProduct,
            categoryId: CATEGORIES[0].id,
          } as ProductWithColors)
        else navigate(ProductCategoriesLink.MAKEUP)
      } else {
        updateProductItem({
          ...initialProductItem,
          categoryId: CATEGORIES[0].id,
          merchant: userInfo.merchantId,
          colors: [createEmptyColor()],
        })
      }

      setIsLoading(false)
    })()
    return () => void Beauty.reset()
  }, [productId])

  const saveTranslationsAfterProductSave = async (
    productId: string,
    _skuIds: string[],
    translations: ProductTranslationsData
  ) => {
    try {
      const savedProduct = await tintVTOService.productByIdV2(productId)

      const skuToSkuIdMap: Record<string, string> = {}
      savedProduct.colors?.forEach((color) => {
        if (color.skuId && color.sku) skuToSkuIdMap[color.sku] = color.skuId
        if (color.skuId) skuToSkuIdMap[color.skuId] = color.skuId
      })

      const updatedSkuNames: Record<string, any[]> = {}
      Object.entries(translations.skuNames || {}).forEach(([key, skuTranslations]) => {
        let actualSkuId = skuToSkuIdMap[key]

        if (!actualSkuId) {
          const matchingColor = savedProduct.colors?.find(
            (c) => (c.skuId || c.sku) === key || c.sku === key
          )
          if (matchingColor?.skuId) actualSkuId = matchingColor.skuId
        }

        if (!actualSkuId) actualSkuId = key

        if (actualSkuId && skuTranslations) updatedSkuNames[actualSkuId] = skuTranslations
      })

      const productTranslationsToSave = (translations.productName || []).filter(
        (t) => t.name && t.name.trim() && t.code !== "en"
      )
      if (productTranslationsToSave.length > 0)
        await tintVTOService.saveProductTranslation(
          productId,
          productItem.merchant || userInfo.merchantId,
          productTranslationsToSave
        )

      const skuTranslationsToSave: Array<{ skuId: string; translations: any[] }> = []
      Object.entries(updatedSkuNames).forEach(([skuId, skuTranslations]) => {
        const translationsWithNames = (skuTranslations || []).filter(
          (t) => t.name && t.name.trim() && t.code !== "en"
        )
        if (translationsWithNames.length > 0)
          skuTranslationsToSave.push({ skuId, translations: translationsWithNames })
      })

      if (skuTranslationsToSave.length > 0)
        await Promise.all(
          skuTranslationsToSave.map(({ skuId, translations: skuTrans }) =>
            tintVTOService.saveSKUTranslation(
              skuId,
              productItem.merchant || userInfo.merchantId,
              skuTrans,
              productId
            )
          )
        )

      setSavedLanguages(productTranslationsToSave.map((t) => t.code))

      if (productId) await refetchTranslations()
    } catch (error) {
      console.error("Failed to save translations:", error)
      showAlert({
        error: true,
        text: getGenericUserErrorMessage(t),
      })
    }
  }

  const {
    isPending,
    mutate: saveProductMutation,
    mutateAsync: saveProductMutationAsync,
  } = useSaveProductMutation(
    userInfo.merchantId || "",
    ProductCategories.MAKEUP,
    saveTranslationsAfterProductSave,
    { navigateOnSuccess: Boolean(add), showSuccessToast: Boolean(add) }
  )

  const currentProductSnapshot = useMemo(() => createProductSnapshot(productItem), [productItem])
  const hasUnsavedChanges =
    !add &&
    !isLoading &&
    savedProductSnapshot !== null &&
    currentProductSnapshot !== savedProductSnapshot

  useEffect(() => {
    if (add || isLoading || savedProductSnapshot !== null) return
    setSavedProductItem(cloneProductItem(productItem))
    setSavedProductSnapshot(createProductSnapshot(productItem))
  }, [add, isLoading, productItem, savedProductSnapshot])

  const handleRemoveColor = (colorId: string) => {
    const nextColors = productItem.colors.filter((color) => color.id !== colorId)
    updateProductItem({
      colors: nextColors,
    })
    if (activeColorId === colorId) setActiveColorId(nextColors[0]?.id ?? null)
  }

  const handleAddColor = () => {
    const newColor = createEmptyColor()
    updateProductItem({
      colors: [...productItem.colors, newColor],
    })
    if (!activeColorId) setActiveColorId(newColor.id)
  }

  const handleColorChanged = (colorId: string, key: "name" | "sku" | "color", val: string) => {
    setProductItem((prev) => ({
      ...prev,
      colors: prev.colors.map((c) => (c.id === colorId ? { ...c, [key]: val } : c)),
    }))
    setActiveColorId(colorId)
  }

  const handleFileChange = (file: File) => {
    const imageUrl = URL.createObjectURL(file)
    updateProductItem({
      file,
      cover: imageUrl,
    })
  }

  // Try-on visibility is product-level and persists ONLY via the dedicated
  // toggleProductVisibility endpoint (the product save path does not carry `visible`),
  // so flip it immediately like the products list — patch savedProductItem too so a
  // later Discard (which clones it) stays in sync with the backend.
  const handleVisibleChange = async (next: boolean) => {
    if (!productId) return
    const prev = Boolean(productItem.visible)
    setVisibilityLoading(true)
    setProductItem((current) => ({ ...current, visible: next }))
    setSavedProductItem((current) => (current ? { ...current, visible: next } : current))
    try {
      const result = await tintVTOService.toggleProductVisibility(productId, next)
      if ((result as { errors?: unknown }).errors) throw new Error("toggle failed")
    } catch {
      setProductItem((current) => ({ ...current, visible: prev }))
      setSavedProductItem((current) => (current ? { ...current, visible: prev } : current))
      showAlert({ error: true, text: t("productTableRow.alertVisibilityUpdateFailed") })
    } finally {
      setVisibilityLoading(false)
    }
  }

  const handleSave = async () => {
    const translationsToSave = add ? pendingTranslations : translations

    const hasProductTranslations =
      translationsToSave?.productName?.some((t) => t.name && t.name.trim() && t.code !== "en") ||
      false

    const hasSkuTranslations =
      translationsToSave?.skuNames &&
      Object.keys(translationsToSave.skuNames).length > 0 &&
      Object.values(translationsToSave.skuNames).some((skuTrans) =>
        skuTrans?.some((t) => t.name && t.name.trim() && t.code !== "en")
      )

    const hasTranslationData = translationsModified || hasProductTranslations || hasSkuTranslations

    const productToSave = {
      ...(productItem as ProductV2),
      ...(hasTranslationData ? { pendingTranslations: translationsToSave } : {}),
    }
    try {
      const editedProduct = cloneProductItem(productItem)
      const activeColorKey = getStableColorKey(
        editedProduct.colors.find((color) => color.id === activeColorId) || editedProduct.colors[0]
      )
      const saveResult = await saveProductMutationAsync(productToSave)
      if (add) {
        navigate(ProductCategoriesLink.MAKEUP)
        return
      }

      const savedProductId = saveResult.savedProductId ?? productId
      if (!savedProductId) {
        showAlert({ error: true, text: t("productPage.saveVerifyError") })
        return
      }

      let normalizedSavedProduct: ProductWithColors
      try {
        const savedProduct = await tintVTOService.productByIdV2(savedProductId)
        normalizedSavedProduct = normalizeLoadedProduct(savedProduct)
      } catch {
        showAlert({ error: true, text: t("productPage.saveVerifyError") })
        return
      }

      if (!serverProductMatchesEditedFields(editedProduct, normalizedSavedProduct)) {
        showAlert({ error: true, text: t("productPage.saveVerifyError") })
        return
      }

      setProductItem(normalizedSavedProduct)
      setActiveColorId(
        normalizedSavedProduct.colors.find((color) => getStableColorKey(color) === activeColorKey)
          ?.id ??
          normalizedSavedProduct.colors[0]?.id ??
          null
      )
      applySavedProductBaseline(normalizedSavedProduct)
      setTranslationsModified(false)
      setShowValidation(false)
      showAlert({
        error: false,
        text: t("productPage.saveSuccess", {
          name: normalizedSavedProduct.name,
          action: t("productPage.updated"),
        }),
      })
    } catch {
      // Error toast already shown by mutation's onError.
    }
  }

  const handleDiscard = () => {
    if (!hasUnsavedChanges || !savedProductItem) return
    setProductItem(cloneProductItem(savedProductItem))
    setActiveColorId(savedProductItem.colors[0]?.id ?? null)
    setErrors(null)
    setShowValidation(false)
  }

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowLeaveDialog(true)
      return
    }
    navigate(ProductCategoriesLink.MAKEUP)
  }

  if (isLoading)
    return (
      <div data-testid="makeup-editor-page">
        <FixedHeaderLayout
          title={pageHeaderTitle}
          onBack={() => navigate(ProductCategoriesLink.MAKEUP)}
          onFirstButtonClicked={() => navigate(ProductCategoriesLink.MAKEUP)}
          onSecondButtonClicked={() => saveProductMutation(productItem as ProductV2)}
          onSecondButtonLoading={isPending}
          isFirstButtonDisabled={true}
          secondButtonDisabled={true}
          firstButtonText={t("productPage.discard")}
          secondButtonText={t("productPage.saveChanges")}
          validateSecondButton={validateForm}
        />
        <div className="px-6 pb-6 overflow-hidden" data-testid="makeup-editor-loading">
          <PageLoader />
        </div>
      </div>
    )

  const showMerchantSelect = userInfo.groups.includes("Admin")
  const canAddRemoveVariants = userInfo.integrationType === IntegrationType.COMMON
  const previewDisabled =
    !productItem.coverage ||
    !productItem.region ||
    !productItem.finish ||
    productItem.colors.length < 1
  const activeColorIndex = productItem.colors.findIndex((c) => c.id === activeColorId)
  const rowErrors = (productItem.colors || []).map((_, index) => {
    const row = (displayedErrors?.colors as any)?.[index]
    return row
      ? {
          name: row?.name?._errors as string[] | undefined,
          color: row?.color?._errors as string[] | undefined,
        }
      : undefined
  })

  return (
    <div data-testid="makeup-editor-page">
      <FixedHeaderLayout
        title={pageHeaderTitle}
        onBack={add ? () => navigate(ProductCategoriesLink.MAKEUP) : handleBack}
        onFirstButtonClicked={add ? () => navigate(ProductCategoriesLink.MAKEUP) : handleDiscard}
        onSecondButtonClicked={handleSave}
        onSecondButtonLoading={isPending}
        isFirstButtonDisabled={!add && (!hasUnsavedChanges || isPending)}
        secondButtonDisabled={!add && !hasUnsavedChanges}
        firstButtonText={t("productPage.discard")}
        secondButtonText={t("productPage.saveChanges")}
        validateSecondButton={() => {
          const isValid = validateForm()
          if (!isValid) setShowValidation(true)
          return isValid
        }}
      />

      <div className="mx-auto max-w-[1440px] p-6" data-testid="makeup-editor-content">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,800px)_minmax(420px,1fr)]">
          <div className="space-y-3" data-testid="makeup-editor-form">
            <MakeupMetadataCard
              name={productItem.name}
              typeId={productItem.typeId}
              region={productItem.region}
              finish={productItem.finish}
              coverage={productItem.coverage}
              cover={productItem.cover}
              merchant={productItem.merchant}
              sortedTypes={sortedTypes}
              regions={getRegionsFrom(productItem)}
              finishes={getFinishesFrom(productItem)}
              coverages={getCoveragesFrom(productItem)}
              merchantOptions={merchantIds}
              showMerchantSelect={showMerchantSelect}
              visible={Boolean(productItem.visible)}
              onVisibleChange={(value) => void handleVisibleChange(value)}
              visibilityDisabled={Boolean(add) || !productId || visibilityLoading}
              visibilityDisabledHint={t("productPage.tryOnSaveFirstHint")}
              autoFocusName={Boolean(add)}
              nameErrors={displayedErrors?.name?._errors}
              typeErrors={displayedErrors?.typeId?._errors}
              regionErrors={displayedErrors?.region?._errors}
              finishErrors={displayedErrors?.finish?._errors}
              coverageErrors={displayedErrors?.coverage?._errors}
              coverErrors={displayedErrors?.cover?._errors}
              merchantErrors={displayedErrors?.merchant?._errors}
              onNameChange={(value) => updateProductItem({ name: value })}
              onTypeChange={(value) =>
                updateProductItem({ typeId: value, region: "", finish: "", coverage: "" })
              }
              onRegionChange={(value) =>
                updateProductItem({ region: value, finish: "", coverage: "" })
              }
              onFinishChange={(value) => updateProductItem({ finish: value })}
              onCoverageChange={(value) => updateProductItem({ coverage: value })}
              onCoverChange={handleFileChange}
              onMerchantChange={(value) => updateProductItem({ merchant: value })}
            />

            <MakeupVariantsCard
              colors={productItem.colors}
              rowErrors={rowErrors}
              globalErrors={displayedErrors?.colors?._errors}
              canAddRemove={canAddRemoveVariants}
              onChange={handleColorChanged}
              onRemove={handleRemoveColor}
              onAdd={handleAddColor}
            />
          </div>

          <div>
            <div className="sticky top-20" data-testid="makeup-editor-preview-panel">
              <MakeupPreviewPanel
                productItem={productItem as ProductV2}
                disabled={previewDisabled}
                activeColorIndex={activeColorIndex}
                onActiveColorIndexChange={(index) => {
                  setActiveColorId(productItem.colors[index]?.id || null)
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {(productId || add) && (
        <ProductTranslationsModal
          isOpen={isTranslationsModalOpen}
          onClose={() => setIsTranslationsModalOpen(false)}
          productId={productId || ""}
          merchantId={productItem.merchant || userInfo.merchantId}
          skuIds={productItem.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []}
          defaultProductName={productItem.name}
          defaultSkuNames={
            productItem.colors?.reduce((acc, c) => {
              const skuId = c.skuId || c.sku
              if (skuId) acc[skuId] = c.name
              return acc
            }, {} as Record<string, string>) || {}
          }
          defaultSkuNumbers={
            productItem.colors?.reduce((acc, c) => {
              const skuId = c.skuId || c.sku
              if (skuId) acc[skuId] = c.sku
              return acc
            }, {} as Record<string, string>) || {}
          }
          translations={add ? pendingTranslations : translations}
          onChange={(newTranslations) => {
            setTranslationsModified(true)
            if (add) setPendingTranslations(newTranslations)
            else setTranslations(newTranslations)
          }}
          onSave={
            !add && productId
              ? async () => {
                  const skuIds =
                    productItem.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
                  await saveTranslationsAfterProductSave(productId, skuIds, translations)
                  setTranslationsModified(false)
                }
              : undefined
          }
          savedLanguages={savedLanguages}
        />
      )}
      <UnsavedChangesDialog
        isOpen={showLeaveDialog}
        onClose={() => setShowLeaveDialog(false)}
        onLeave={() => {
          setShowLeaveDialog(false)
          navigate(ProductCategoriesLink.MAKEUP)
        }}
      />
    </div>
  )
}
